import { FC, useEffect, useState } from 'react'

import * as Sentry from '@sentry/react'
import { Field, Form, Formik, useFormikContext } from 'formik'
import { useIntl } from 'react-intl'

import { Box, Button, THEME, ThemeProvider, Typography, useTheme } from '@themenu/design-system'

import { FormikFieldInput } from '../../components/Form/FormikFieldInput'
import { PushButtonToBottom, StyledFormContainer } from '../../components/Shared/Form'
import { Title } from '../../components/Shared/Title'
import { useMfaValidators } from '../../hooks/Form/useMfaFormValidators'
import { environmentVariables } from '../../utils/environmentVariables'

const COUNTDOWN_DURATION = parseInt(environmentVariables('COUNTDOWN_DURATION'), 10)

export type MfaFormValues = {
  authenticationCode?: string
  secondaryFactor?: string
}

type Props = {
  onSubmit: (values: MfaFormValues) => Promise<void>
}

enum CodeState {
  SENT = 'SENT',
  NOT_RECEIVED = 'NOT_RECEIVED',
  RESEND_AVAILABLE = 'RESEND_AVAILABLE',
  RESENT = 'RESENT',
  ERROR = 'ERROR',
}

enum CodeEventType {
  COUNTDOWN,
  NOT_RECEIVED_CLICKED,
  RESEND_CLICKED,
}

type CodeEvent = {
  type: CodeEventType
  value?: string
}

enum SecondaryFactor {
  EMAIL = 'email',
  SMS = 'sms',
}

const AutoSubmitOtpButton: FC = () => {
  const intl = useIntl()
  // Grab values,  submitForm  and resetForm from context
  const { dirty, isValid, isSubmitting, submitForm, values } = useFormikContext<MfaFormValues>()

  useEffect(() => {
    if (isValid && values.authenticationCode?.length) {
      submitForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid])

  return (
    <Button
      aria-label="submit"
      type="submit"
      variant="contrast"
      fullWidth
      disabled={!dirty || !isValid || isSubmitting}
    >
      {intl.formatMessage({ id: 'sign_in.form.mfa.submit' })}
    </Button>
  )
}

export const MfaForm: FC<Props> = ({ onSubmit }) => {
  const intl = useIntl()
  const theme = useTheme()
  const { initialValues, validationSchema } = useMfaValidators()

  const [secondaryFactor, setSecondaryFactor] = useState(SecondaryFactor.EMAIL)
  const [countDownValue, setCountDownValue] = useState(COUNTDOWN_DURATION)
  const [currentCodeState, setCurrentCodeState] = useState(CodeState.SENT)

  //          SENT
  //           │
  // countdown │
  //           │
  //           ▼          countdown
  //      NOT RECEIVED ◄────────────┐
  //           │                    │
  //           │ click              │
  //           │                    │
  //           ▼                    │
  //    RESEND AVAILABLE ───────► RESENT  ┌──► ERROR
  //                      click           │
  //                         │            │
  //                         └────────────┘
  // handle code state event
  const handleEvent = async (event: CodeEvent): Promise<void> => {
    switch (event.type) {
      case CodeEventType.COUNTDOWN:
        setCurrentCodeState(CodeState.NOT_RECEIVED)
        break
      case CodeEventType.NOT_RECEIVED_CLICKED:
        setCurrentCodeState(CodeState.RESEND_AVAILABLE)
        break
      case CodeEventType.RESEND_CLICKED:
        setCountDownValue(COUNTDOWN_DURATION)
        setCurrentCodeState(CodeState.RESENT)
        switch (event.value) {
          case SecondaryFactor.SMS:
            setSecondaryFactor(SecondaryFactor.SMS)
            break
          default:
            setSecondaryFactor(SecondaryFactor.EMAIL)
        }
        try {
          await onSubmit({ secondaryFactor: event.value })
        } catch (err) {
          Sentry.captureException(err)
          setCurrentCodeState(CodeState.ERROR)
        }
    }
  }

  // decrement countdown every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCountDownValue((c) => {
        return Math.max(-1, c - 1)
      })
    }, 1000)
    return () => clearInterval(interval)
  })

  // trigger COUNTDOWN event and stop countdown when it's reached 0
  useEffect(() => {
    if (countDownValue === 0) {
      handleEvent({ type: CodeEventType.COUNTDOWN })
    }
  }, [countDownValue]) // eslint-disable-line react-hooks/exhaustive-deps

  const codeStateComponents = {
    [CodeState.SENT]: (
      <Typography variant="footnote" color={theme.colors.content.subdued} p={4} tabIndex={-1}>
        {intl.formatMessage({ id: 'sign_in.form.mfa.feedback.not_received' })} ({countDownValue})
      </Typography>
    ),
    [CodeState.NOT_RECEIVED]: (
      <Typography
        variant="footnote"
        color={theme.colors.content.subdued}
        p={4}
        tabIndex={-1}
        type="reset"
        component="button"
        onClick={() => handleEvent({ type: CodeEventType.NOT_RECEIVED_CLICKED })}
      >
        {intl.formatMessage({ id: 'sign_in.form.mfa.feedback.not_received' })}
      </Typography>
    ),
    [CodeState.RESEND_AVAILABLE]: (
      <>
        <Typography
          variant="footnote"
          color={theme.colors.content.subdued}
          p={4}
          tabIndex={-1}
          type="reset"
          component="button"
          onClick={() => handleEvent({ type: CodeEventType.RESEND_CLICKED })}
        >
          {intl.formatMessage({ id: 'sign_in.form.mfa.resend_code_by_email' })}
        </Typography>
        <Typography
          variant="footnote"
          color={theme.colors.content.subdued}
          p={4}
          tabIndex={-1}
          type="reset"
          component="button"
          onClick={() => handleEvent({ type: CodeEventType.RESEND_CLICKED, value: SecondaryFactor.SMS })}
        >
          {intl.formatMessage({ id: 'sign_in.form.mfa.resend_code_by_sms' })}
        </Typography>
      </>
    ),
    [CodeState.RESENT]: (
      <>
        <Typography variant="footnote" color={theme.colors.content.subdued} p={4}>
          {intl.formatMessage({ id: 'sign_in.form.mfa.feedback.code_resent' }, { secondaryFactor })}
        </Typography>
        <Typography variant="footnote" color={theme.colors.content.subdued} p={4}>
          {intl.formatMessage({ id: 'sign_in.form.mfa.feedback.not_received' })} ({countDownValue})
        </Typography>
      </>
    ),
    [CodeState.ERROR]: (
      <Typography variant="footnote" color={theme.colors.content.critical} p={4}>
        {intl.formatMessage({ id: 'sign_in.form.mfa.feedback.resend_failed' })}
      </Typography>
    ),
  }

  return (
    <Box mt={24} display="flex" flex="1" flexDirection="column">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { resetForm }) => {
          await onSubmit(values)
          await handleEvent({ type: CodeEventType.COUNTDOWN })
          resetForm()
        }}
      >
        {({ setFieldValue, isSubmitting }) => (
          <Form name="sign-in-mfa-form">
            <StyledFormContainer>
              <Title>{intl.formatMessage({ id: 'sign_in.form.mfa.title' })}</Title>
              <Typography variant="body" mt={8} color={theme.colors.content.subdued}>
                {intl.formatMessage({ id: 'sign_in.form.mfa_message' }, { secondaryFactor })}
              </Typography>
              <ThemeProvider theme={THEME.LIGHT}>
                <>
                  <Field
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    mt={16}
                    name="authenticationCode"
                    component={FormikFieldInput}
                    label={intl.formatMessage({
                      id: 'sign_in.form.mfa',
                    })}
                    labelOption="sticky"
                    autocomplete="one-time-code"
                    type="text"
                    inputMode="numeric"
                    disabled={isSubmitting}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('authenticationCode', event.target?.value.trim())
                    }}
                  />
                  <ThemeProvider theme={THEME.DARK}>{codeStateComponents[currentCodeState]}</ThemeProvider>
                  <PushButtonToBottom>
                    <AutoSubmitOtpButton />
                  </PushButtonToBottom>
                </>
              </ThemeProvider>
            </StyledFormContainer>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
